import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, message } from 'antd';
import { ArrowRightOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { ordersApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import OrderForm from '../../components/orders/OrderForm';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';

const createEmptyItem = () => ({
  item_id: null,
  product_id: null,
  item_name: '',
  quantity: 1,
  unit_id: null,
  sku: '',
  cost_per_item: 0,
  amount: 0,
  item_summary: '',
});

const EditOrder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/orders', { state: buildGoBackState() });
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [options, setOptions] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [initialItems, setInitialItems] = useState([createEmptyItem()]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const [createRes, orderRes] = await Promise.all([
          ordersApi.getCreateData(),
          ordersApi.getById(id),
        ]);
        const data = createRes?.data || {};
        const order = orderRes?.data?.order || {};
        setOptions({
          ...data,
          projects: data.projects || orderRes?.data?.order?.client?.projects || [],
        });
        setInitialValues({
          order_number: Number(String(order.original_order_number || order.order_number || '').replace(/\D/g, '')) || undefined,
          order_date: order.order_date || null,
          client_id: order.client_id,
          project_id: order.project_id || undefined,
          company_address_id: order.company_address_id || undefined,
          shipping_address: order.shipping_address || null,
          status: order.status || 'pending',
          bank_account_id: order.bank_account_id || undefined,
          category_id: order.category_id || undefined,
          discount_value: Number(order.discount || 0),
          discount_type: order.discount_type || 'percent',
          note: order.note || '',
        });
        const mappedItems = (order.items || []).map((it) => ({
          item_id: it.id,
          product_id: it.product_id,
          item_name: it.item_name || '',
          quantity: Number(it.quantity || 0),
          unit_id: it.unit_id,
          sku: it.sku || '',
          cost_per_item: Number(it.unit_price || 0),
          amount: Number(it.amount || 0),
          item_summary: it.item_summary || '',
        }));
        setInitialItems(mappedItems.length ? mappedItems : [createEmptyItem()]);
      } catch (error) {
        message.error('خطا در دریافت اطلاعات ویرایش سفارش');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleSubmit = async (payload) => {
    setSubmitLoading(true);
    try {
      await ordersApi.update(id, payload);
      message.success('سفارش با موفقیت ویرایش شد');
      goBackToList();
    } catch (error) {
      message.error('خطا در ویرایش سفارش');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                  <ShoppingCartOutlined className="text-emerald-600 dark:text-emerald-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0">ویرایش سفارش</h1>
                </div>
              </div>
              <Button icon={<ArrowRightOutlined />} onClick={goBackToList}>
                بازگشت
              </Button>
            </div>
          </div>
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            <Card className="shadow-lg border-0 bg-white dark:bg-gray-800">
              {loading ? (
                <TableSkeleton rows={6} columns={2} />
              ) : (
                <OrderForm
                  options={options}
                  initialValues={initialValues}
                  initialItems={initialItems}
                  isEdit
                  submitLoading={submitLoading}
                  onSubmit={handleSubmit}
                />
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditOrder;
